// Login-time recovery code page (Part 2 §13.7).
//
// `/login/recovery` is the alternate path from /login/mfa for users who
// lost their authenticator. Consuming a recovery code single-uses it and
// flags `User.mustReenrollMfa=true` (handled inside the auth service).

import { useState, useEffect, type FormEvent } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { trpc } from '../api/trpc';
import { useAuth } from '../hooks/useAuth';
import { CadeAvatar } from '../components/CadeAvatar';

interface LocationState { challengeToken?: string }

export function LoginRecoveryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hydrate } = useAuth();
  const state = (location.state as LocationState | null) ?? {};
  const challengeToken = state.challengeToken;

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const consume = trpc.auth.consumeLoginRecoveryCode.useMutation();

  useEffect(() => {
    if (!challengeToken) navigate('/login', { replace: true });
  }, [challengeToken, navigate]);

  if (!challengeToken) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = code.trim();
    if (!trimmed) { setError('Enter a recovery code.'); return; }
    try {
      const result = await consume.mutateAsync({ challengeToken: challengeToken!, code: trimmed });
      hydrate({ token: result.token, userId: result.userId, tenantId: result.tenantId });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid recovery code.');
    }
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <CadeAvatar size={80} />
          <span className="login-logo-text">Recovery code</span>
        </div>
        <form className="login-form" onSubmit={(e) => { void handleSubmit(e); }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted, #888)' }}>
            Enter one of the recovery codes you saved when you set up MFA. Each code can be used once;
            after sign-in you'll be prompted to re-enroll MFA.
          </p>
          <div className="form-field">
            <label htmlFor="login-recovery-code">Recovery code</label>
            <input
              id="login-recovery-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              placeholder="xxxx-xxxx-xxxx"
            />
          </div>
          {error && <p className="login-error" data-testid="login-recovery-error">{error}</p>}
          <button
            type="submit"
            disabled={consume.isPending}
            className="btn-recommended"
            data-testid="login-recovery-submit"
          >
            {consume.isPending
              ? <><span className="btn-spinner" aria-hidden="true" data-testid="login-recovery-spinner" />Verifying…</>
              : 'Sign in with recovery code'}
          </button>
        </form>
        <p style={{ marginTop: 16, fontSize: 13, textAlign: 'center' }}>
          <Link to="/login/mfa" state={{ challengeToken }}>
            Back to authenticator code
          </Link>
        </p>
      </div>
    </div>
  );
}
